import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  MoreVertical, 
  FileEdit, 
  Trash2, 
  Eye, 
  Star, 
  Undo,
  FileText
} from "lucide-react";
import { CaseStudyItem } from "@/types/case-study";
import { useCaseStudies, useCaseStudy } from "@/hooks/useCaseStudy";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import { useProfileContext } from "@/contexts/ProfileContext";
import { cleanWavyUnderlines } from "@/utils/cleanWavyUnderlines";

const stripHtmlTags = (html: string): string => {
  if (!html) {
    return "";
  }
  return html.replace(/<[^>]*>/g, " ");
};

const getPlainDescription = (description: string): string => {
  const cleaned = cleanWavyUnderlines(description || "");
  const stripped = stripHtmlTags(cleaned).replace(/\s+/g, " ").trim();
  return stripped;
};

interface CaseStudyListProps {
  onEdit: (caseStudy: CaseStudyItem) => void;
  onPreview: (caseStudy: CaseStudyItem) => void;
  onCreateNew: () => void;
}

export const CaseStudyList: React.FC<CaseStudyListProps> = ({
  onEdit,
  onPreview,
  onCreateNew,
}) => {
  const { user } = useAuth();
  const { profile } = useProfileContext();
  const { data: caseStudies = [], isLoading, error } = useCaseStudies(profile?.id);
  const { deleteCaseStudy, updateCaseStudy } = useCaseStudy();
  const autoFeaturedRef = React.useRef<string | null>(null);

  const handleDeleteClick = (caseStudyId: string) => {
    if (window.confirm('Are you sure you want to delete this case study? This action cannot be undone.')) {
      handleDeleteConfirm(caseStudyId);
    }
  };

  const handleDeleteConfirm = async (caseStudyId: string) => {
    try {
      await deleteCaseStudy.mutateAsync(caseStudyId);
    } catch (error) {
      console.error('Error deleting case study:', error);
    }
  };

  const handleToggleFeatured = async (caseStudyId: string) => {
    const caseStudy = caseStudies.find(cs => cs.id === caseStudyId);
    if (caseStudy) {
      try {
        await updateCaseStudy.mutateAsync({
          id: caseStudyId,
          is_featured: !caseStudy.is_featured,
        });
      } catch (error) {
        console.error('Error toggling featured status:', error);
      }
    }
  };

  React.useEffect(() => {
    if (isLoading || caseStudies.length !== 1) {
      if (caseStudies.length !== 1) {
        autoFeaturedRef.current = null;
      }
      return;
    }

    const onlyCaseStudy = caseStudies[0];
    if (onlyCaseStudy.status !== "published") {
      autoFeaturedRef.current = null;
      return;
    }

    if (onlyCaseStudy.is_featured) {
      autoFeaturedRef.current = null;
      return;
    }

    if (autoFeaturedRef.current === onlyCaseStudy.id) {
      return;
    }

    autoFeaturedRef.current = onlyCaseStudy.id;
    updateCaseStudy
      .mutateAsync({
        id: onlyCaseStudy.id,
        is_featured: true,
      })
      .catch((error) => {
        console.error("Error auto featuring case study:", error);
        autoFeaturedRef.current = null;
      });
  }, [caseStudies, isLoading, updateCaseStudy]);

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="h-8 w-8 text-gray-400 animate-pulse" />
        </div>
        <h3 className="text-lg font-semibold mb-2">Loading Case Studies...</h3>
        <p className="text-gray-600">Please wait while we fetch your case studies.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="h-8 w-8 text-red-400" />
        </div>
        <h3 className="text-lg font-semibold mb-2">Error Loading Case Studies</h3>
        <p className="text-gray-600 mb-4">
          There was an error loading your case studies. Please try again.
        </p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  if (caseStudies.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold mb-2">No Case Studies Yet</h3>
        <p className="text-gray-600 mb-4">
          Create your first case study to showcase your design process and impact.
        </p>
        <Button variant="outline" onClick={onCreateNew}>
          Create Case Study
        </Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {caseStudies.map((caseStudy) => (
        <Card key={caseStudy.id} className="overflow-hidden transition-all duration-200 group relative h-full">
          {caseStudy.is_featured && (
            <div className="absolute top-2 left-2 z-50 flex gap-1">
              <Badge className="text-xs py-1 px-2 rounded-md bg-yellow-500 text-yellow-900 hover:bg-yellow-500/80">
                <Star className="h-3 w-3 mr-1 fill-yellow-900" /> Featured
              </Badge>
            </div>
          )}
          
          <div
            className="relative aspect-video w-full overflow-hidden bg-muted cursor-pointer"
            onClick={() => onEdit(caseStudy)}
          >
            {caseStudy.cover_image ? (
              <img
                src={caseStudy.cover_image}
                alt={caseStudy.title}
                className="h-full w-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = '/placeholder.svg';
                }}
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                1600 × 900
              </div>
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200" />
          </div>

          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">{caseStudy.title}</h3>
                  {caseStudy.status === "draft" && (
                    <Badge variant="secondary">Draft</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-3 overflow-hidden break-words">
                  {getPlainDescription(caseStudy.description) || "No description provided."}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEdit(caseStudy)}>
                      <FileEdit className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onPreview(caseStudy)}>
                      <Eye className="mr-2 h-4 w-4" />
                      Preview
                    </DropdownMenuItem>
                    {caseStudy.status === "published" && (
                      <DropdownMenuItem onClick={async () => {
                        try {
                          await updateCaseStudy.mutateAsync({
                            id: caseStudy.id,
                            status: "draft",
                            is_featured: false,
                          });
                        } catch (error) {
                          console.error('Error updating status:', error);
                        }
                      }}>
                        <Undo className="mr-2 h-4 w-4" />
                        Revert to Draft
                      </DropdownMenuItem>
                    )}
                    {caseStudy.status === "draft" && (
                      <DropdownMenuItem onClick={async () => {
                        try {
                          await updateCaseStudy.mutateAsync({
                            id: caseStudy.id,
                            status: "published",
                          });
                        } catch (error) {
                          console.error('Error updating status:', error);
                        }
                      }}>
                        <FileEdit className="mr-2 h-4 w-4" />
                        Publish
                      </DropdownMenuItem>
                    )}
                    {caseStudy.is_featured ? (
                      <DropdownMenuItem disabled>
                        <Star className="mr-2 h-4 w-4 fill-current" />
                        Featured
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={() => handleToggleFeatured(caseStudy.id)}>
                        <Star className="mr-2 h-4 w-4" />
                        Set as Featured
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleDeleteClick(caseStudy.id)}
                      className="text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

    </div>
  );
};
